import type { Row } from '@tanstack/react-table';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import type { IntelligenceSummary } from '~/common/types';

interface YearChartData {
  axis: number;
  [key: string]: number;
}

interface RegionChartData {
  axis: string;
  [key: string]: string | number;
}

export type ChartData = YearChartData | RegionChartData;

export const useChartData = (
  rows: Row<IntelligenceSummary>[],
  axisField: 'constructionYear' | 'region' | 'division',
) => {
  const [result, setResult] = useState<{
    chartData: ChartData[];
    segments: string[];
  }>({ chartData: [], segments: [] });

  useEffect(() => {
    const processData = _.debounce((currentRows: Row<IntelligenceSummary>[]) => {
      const filteredData = currentRows.map((row) => row.original);

      const uniqueSegments = _.chain(filteredData)
        .map((item) => item.project.planStatus)
        .uniq()
        .compact()
        .value();

      const axisGroups = _.groupBy(filteredData, (item) => {
        if (axisField === 'constructionYear') {
          const year = item.project.constructionYear;
          return year && !Number.isNaN(Number(year)) ? year : 'Unset';
        } else if (axisField === 'division') {
          return item.pge?.division || null;
        } else {
          return item.pge?.region || null;
        }
      });

      // remove the null key if it exists
      if (axisField === 'region' || axisField === 'division') {
        delete axisGroups['null'];
      }

      const chartData = _.map(axisGroups, (group, axisValue) => {
        const segmentTotals = _.reduce(
          uniqueSegments,
          (acc, segment) => {
            acc[segment] = _.chain(group)
              .filter((item) => item.project.planStatus === segment)
              .sumBy((item) => item.gasDistributionLength || 0)
              .value();
            return acc;
          },
          {} as { [key: string]: number },
        );

        if (axisField === 'constructionYear') {
          return {
            // keep 'Unset' as string, parse valid years as numbers
            axis: axisValue === 'Unset' ? 'Unset' : parseInt(axisValue, 10),
            ...segmentTotals,
          } as YearChartData;
        } else if (axisField === 'division') {
          return {
            axis: axisValue,
            ...segmentTotals,
          } as RegionChartData;
        } else {
          return {
            axis: axisValue,
            ...segmentTotals,
          } as RegionChartData;
        }
      });

      let _chartData: ChartData[] = [];
      if (axisField === 'constructionYear') {
        _chartData = _.sortBy(chartData, (item) => (item.axis === 'Unset' ? Infinity : Number(item.axis)));
      } else {
        _chartData = chartData;
      }

      setResult({
        chartData: _chartData,
        segments: uniqueSegments,
      });
    }, 300);

    processData(rows);
    return () => processData.cancel();
  }, [rows, axisField]);

  return result;
};
